/**
 * Pure timer engine: (state, command, now) -> new state | null.
 *
 * Returning `null` means the command is a no-op for the current state
 * (e.g. START while already running). No-ops never bump the version, which
 * makes accidental double taps harmless and avoids needless sync traffic.
 */
import { PRESETS, remainingAt, type PresetId, type TimerState } from './state.js';

export type Command =
  | { type: 'SET_PRESET'; presetId: PresetId }
  | { type: 'START' }
  | { type: 'PAUSE' }
  | { type: 'RESET' };

export type CommandType = Command['type'];

export function applyCommand(
  state: TimerState,
  command: Command,
  now: number,
  originId: string,
): TimerState | null {
  const next = transition(state, command, now);
  if (next === null) return null;
  return { ...next, version: state.version + 1, updatedAt: now, originId };
}

function transition(state: TimerState, command: Command, now: number): TimerState | null {
  switch (command.type) {
    case 'SET_PRESET': {
      const preset = PRESETS[command.presetId];
      // Re-selecting the current preset while idle at full duration is a no-op.
      if (
        state.presetId === preset.id &&
        state.status === 'idle' &&
        state.remainingMs === preset.durationMs
      ) {
        return null;
      }
      return {
        ...state,
        presetId: preset.id,
        durationMs: preset.durationMs,
        status: 'idle',
        remainingMs: preset.durationMs,
        endAt: null,
      };
    }
    case 'START': {
      if (state.status === 'running') return null;
      const remaining = remainingAt(state, now);
      // A finished (0 ms) or exhausted timer cannot be started; choose a preset or reset.
      if (remaining <= 0) return null;
      return { ...state, status: 'running', remainingMs: remaining, endAt: now + remaining };
    }
    case 'PAUSE': {
      if (state.status !== 'running') return null;
      const remaining = remainingAt(state, now);
      // Pausing an already finished timer changes nothing visible; keep it finished.
      if (remaining <= 0) return null;
      return { ...state, status: 'paused', remainingMs: remaining, endAt: null };
    }
    case 'RESET': {
      if (state.status === 'idle' && state.remainingMs === state.durationMs) return null;
      return { ...state, status: 'idle', remainingMs: state.durationMs, endAt: null };
    }
  }
}
